import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const useCountUp = (end: number, duration: number = 1500, enabled: boolean = true) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          let current = 0;
          const step = end / (duration / 16);
          const timer = setInterval(() => {
            current += step;
            if (current >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration, started, enabled]);

  return { count, ref };
};

interface StatItem {
  value: string;
  label: string;
  isNumeric?: boolean;
}

const AnimatedStat: React.FC<{ stat: StatItem }> = ({ stat }) => {
  const isNumeric = !!stat.isNumeric;
  const numericValue = isNumeric ? parseInt(stat.value, 10) : 0;
  const { count, ref } = useCountUp(numericValue, 1500, isNumeric);

  return (
    <div ref={isNumeric ? ref : undefined} className="hp-social-proof-item">
      <span className="hp-social-proof-value">
        {isNumeric ? count : stat.value}
      </span>
      <span className="hp-social-proof-label">{stat.label}</span>
    </div>
  );
};

const SocialProofBar: React.FC = () => {
  const { t } = useTranslation();
  const stats = t('socialProof.stats', { returnObjects: true }) as StatItem[];

  return (
    <div className="hp-social-proof">
      <div className="hp-social-proof-inner">
        {stats.map((s) => (
          <AnimatedStat key={s.label} stat={s} />
        ))}
      </div>
    </div>
  );
};

export default SocialProofBar;
